/**
 * Shared human-readable descriptions for recorded activity events.
 * Keep wording centralized for timeline, live feed, and reports.
 * Mirrors backend/src/services/actionDescription.ts
 */

export type ActionDescriptionInput = {
  eventType?: string | null;
  applicationName?: string | null;
  processName?: string | null;
  windowTitle?: string | null;
  domain?: string | null;
  pageTitle?: string | null;
  uiElementName?: string | null;
  uiElementType?: string | null;
  actionName?: string | null;
  browserName?: string | null;
  durationSeconds?: number | null;
};

/** Map common Windows process names to supervisor-friendly labels. */
export function friendlyAppName(raw?: string | null): string | null {
  if (!raw || !String(raw).trim()) return null;
  const name = String(raw).trim();
  const key = name.replace(/\.exe$/i, '').toLowerCase();
  const map: Record<string, string> = {
    msedge: 'Microsoft Edge',
    chrome: 'Google Chrome',
    firefox: 'Mozilla Firefox',
    iexplore: 'Internet Explorer',
    outlook: 'Outlook',
    excel: 'Excel',
    winword: 'Word',
    powerpnt: 'PowerPoint',
    teams: 'Microsoft Teams',
    msteams: 'Microsoft Teams',
    windowsterminal: 'Windows Terminal',
    windowsterminalpreview: 'Windows Terminal',
    powershell: 'PowerShell',
    pwsh: 'PowerShell',
    cmd: 'Command Prompt',
    explorer: 'File Explorer',
    searchhost: 'Windows Search',
    searchapp: 'Windows Search',
    startmenuexperiencehost: 'Start Menu',
    shellexperiencehost: 'Windows Shell',
    applicationframehost: 'Windows App',
    systemsettings: 'Settings',
    taskmgr: 'Task Manager',
    notepad: 'Notepad',
    notepadplus: 'Notepad++',
    code: 'VS Code',
    devenv: 'Visual Studio',
    slack: 'Slack',
    zoom: 'Zoom',
    m365copilot: 'Microsoft 365 Copilot',
    copilot: 'Microsoft Copilot',
    snippingtool: 'Snipping Tool',
    screenclippinghost: 'Screen Snip',
    calculatorapp: 'Calculator',
    photos: 'Photos',
    paint: 'Paint',
    mspaint: 'Paint',
  };
  return map[key] || name;
}

function quote(name: string): string {
  return `"${name.replace(/"/g, "'")}"`;
}

function formatDurationPhrase(seconds?: number | null): string | null {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const s = Math.floor(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM ? `${h}h ${remM}m` : `${h}h`;
}

export function describeActivityAction(input: ActionDescriptionInput): string {
  const type = String(input.eventType || '').toUpperCase();
  const app = friendlyAppName(input.applicationName || input.processName);
  const element = input.uiElementName || input.actionName || null;
  const page = input.pageTitle || null;
  const domain = input.domain || null;
  const duration = formatDurationPhrase(input.durationSeconds);

  switch (type) {
    case 'BUTTON_ACTIVATED':
    case 'CLICK':
      return element
        ? `Clicked ${quote(element)}`
        : (app ? `Clicked in ${app}` : 'Clicked something on screen');
    case 'MENU_OPENED':
      return element ? `Opened menu ${quote(element)}` : 'Opened a menu';
    case 'ITEM_SELECTED':
      return element ? `Selected ${quote(element)}` : 'Selected an item';
    case 'LINK_OPENED':
      return element ? `Opened link ${quote(element)}` : 'Opened a link';
    case 'APP_OPENED':
      return app ? `Opened ${app}` : 'Opened an application';
    case 'APP_CLOSED':
      return app ? `Closed ${app}` : 'Closed an application';
    case 'APP_FOCUSED':
    case 'APP_SWITCHED':
      return app ? `Switched to ${app}` : 'Switched applications';
    case 'FOREGROUND_APP':
      if (app && duration) return `Used ${app} for ${duration}`;
      return app ? `Used ${app}` : 'Used an application';
    case 'WINDOW_CHANGED':
      return app
        ? (input.windowTitle ? `Changed window in ${app}` : `Window changed in ${app}`)
        : 'Window changed';
    case 'TAB_OPENED':
      return 'Opened a browser tab';
    case 'TAB_CLOSED':
      return 'Closed a browser tab';
    case 'TAB_ACTIVATED':
      return page || domain ? `Switched to tab ${page || domain}` : 'Switched browser tabs';
    case 'PAGE_NAVIGATED':
    case 'DOMAIN_CHANGED':
    case 'BROWSER_TAB':
      if (page) return `Opened ${page}`;
      if (domain) return `Visited ${domain}`;
      return 'Browsed the web';
    case 'FILE_OPENED':
      return element ? `Opened file ${quote(element)}` : 'Opened a file';
    case 'FILE_SAVED':
      return element ? `Saved ${quote(element)}` : 'Saved a file';
    case 'IDLE_STARTED':
    case 'IDLE_DETECTED':
      return 'Became idle';
    case 'IDLE_ENDED':
      return 'Returned from idle';
    case 'WINDOWS_LOCKED':
    case 'WORKSTATION_LOCKED':
      return 'Locked Windows';
    case 'WINDOWS_UNLOCKED':
      return 'Unlocked Windows';
    case 'TEKS_AUTH_SUCCESS':
      return 'Signed in to TEKS TIME';
    case 'TEKS_AUTH_FAILURE':
      return 'TEKS TIME sign-in failed';
    case 'CLOCK_IN':
      return 'Clocked In';
    case 'CLOCK_OUT':
      return 'Clocked Out';
    case 'BREAK_START':
      return 'Started Break';
    case 'BREAK_END':
      return 'Returned from Break';
    case 'LUNCH_START':
      return 'Started Lunch';
    case 'LUNCH_END':
      return 'Returned from Lunch';
    case 'BATHROOM_START':
      return 'Started Bathroom';
    case 'BATHROOM_END':
      return 'Returned from Bathroom';
    case 'WORK_RESUMED':
      return 'Resumed work';
    case 'CAMPAIGN_TRANSFER':
      return 'Campaign transfer';
    case 'COVERAGE_STARTED':
      return 'Started coverage assignment';
    case 'COVERAGE_ENDED':
      return 'Ended coverage assignment';
    case 'EMERGENCY_BYPASS_STARTED':
    case 'EMERGENCY_BYPASS_GRANTED':
      return 'Emergency Access started';
    case 'EMERGENCY_BYPASS_ENDED':
      return 'Emergency Access ended';
    default:
      return type ? type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : 'Activity';
  }
}

export function describeActivityContext(input: ActionDescriptionInput): string | null {
  const parts: string[] = [];
  const app = friendlyAppName(input.applicationName || input.processName);
  if (app) parts.push(app);
  if (input.browserName) {
    const browser = friendlyAppName(input.browserName) || input.browserName;
    if (!parts.includes(browser)) parts.push(browser);
  }
  if (input.domain) parts.push(input.domain);
  else if (input.windowTitle) {
    const title = String(input.windowTitle).trim();
    if (title && (!app || title.toLowerCase() !== app.toLowerCase())) {
      parts.push(title);
    }
  }
  return parts.length ? parts.join(' · ') : null;
}

export function actionDescriptionFromEvent(event: {
  event_type?: string | null;
  label?: string | null;
  context?: string | null;
  description?: string | null;
  application_name?: string | null;
  process_name?: string | null;
  window_title?: string | null;
  domain?: string | null;
  page_title?: string | null;
  duration_seconds?: number | null;
  detail?: Record<string, unknown> | null;
}): { label: string; context: string | null } {
  const detail = event.detail || {};
  const input: ActionDescriptionInput = {
    eventType: event.event_type,
    applicationName: event.application_name
      || (detail.application_name as string)
      || null,
    processName: event.process_name
      || (detail.process_name as string)
      || null,
    windowTitle: event.window_title
      || (detail.window_title as string)
      || null,
    domain: event.domain || (detail.domain as string) || null,
    pageTitle: event.page_title || (detail.page_title as string) || null,
    uiElementName: (detail.ui_element_name as string) || null,
    uiElementType: (detail.ui_element_type as string) || null,
    actionName: (detail.action_name as string) || null,
    browserName: (detail.browser_name as string) || null,
    durationSeconds: event.duration_seconds
      ?? (typeof detail.duration_seconds === 'number' ? detail.duration_seconds : null),
  };

  const computed = describeActivityAction(input);
  const rawLabel = event.label || event.description || null;
  // Prefer computed wording when the stored/API label is a raw event type.
  const looksRaw = rawLabel
    && /^[A-Z][A-Z0-9_]+$/.test(String(rawLabel).trim());

  return {
    label: (!rawLabel || looksRaw) ? computed : String(rawLabel),
    context: event.context ?? describeActivityContext(input),
  };
}

/** Collapse noisy APP_FOCUSED + FOREGROUND_APP pairs for the same app near the same time. */
export function dedupeLiveActivityEvents<T extends {
  event_id?: string | null;
  event_type?: string | null;
  application_name?: string | null;
  started_at?: string | null;
  description?: string | null;
}>(events: T[]): T[] {
  const sorted = [...events].sort((a, b) =>
    String(b.started_at || '').localeCompare(String(a.started_at || '')),
  );
  const kept: T[] = [];
  const FOCUS_TYPES = new Set(['APP_FOCUSED', 'APP_SWITCHED', 'FOREGROUND_APP']);

  for (const event of sorted) {
    const type = String(event.event_type || '').toUpperCase();
    if (!FOCUS_TYPES.has(type)) {
      kept.push(event);
      continue;
    }

    const appKey = String(event.application_name || '').toLowerCase();
    const t = event.started_at ? Date.parse(event.started_at) : NaN;
    const duplicate = kept.find((existing) => {
      const existingType = String(existing.event_type || '').toUpperCase();
      if (!FOCUS_TYPES.has(existingType)) return false;
      if (String(existing.application_name || '').toLowerCase() !== appKey) return false;
      const et = existing.started_at ? Date.parse(existing.started_at) : NaN;
      if (!Number.isFinite(t) || !Number.isFinite(et)) return false;
      return Math.abs(t - et) <= 2500;
    });

    if (!duplicate) {
      kept.push(event);
      continue;
    }

    // Prefer APP_FOCUSED ("Switched to …") over FOREGROUND_APP in a near-duplicate pair.
    const dupType = String(duplicate.event_type || '').toUpperCase();
    if (dupType === 'FOREGROUND_APP' && (type === 'APP_FOCUSED' || type === 'APP_SWITCHED')) {
      const idx = kept.indexOf(duplicate);
      if (idx >= 0) kept[idx] = event;
    }
  }

  return kept;
}
